import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { USER_ROLE_ARRAY } from '@pos-app/data';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Sanitizer } from '@angular/core';
@Component({
  selector: 'central-app-manage-help-component',
  templateUrl: './central-app-manage-help-component.component.html',
  styleUrls: ['./central-app-manage-help-component.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageCentralAllHelpComponent implements OnInit, OnDestroy {
  @Input() isEditMode: boolean = false;
  @Input() isCreateNew: boolean = false;
  @Input() helpComponentDetails: any = null;
  @Input() helpComponentRoles: any[] = [];
  @Output() formSubmit = new EventEmitter<FormGroup>();
  @Output() closeActiveModal = new EventEmitter<void>();

  manageHelpForm: FormGroup;
  editorConfig: AngularEditorConfig;
  initialFormValue: any;
  invalid: boolean = true;
  currentPage: number = 1;
  pageSize: number = 4;
  Math = Math; // Expose Math for use in templates
  private unSubscribe$ = new Subject<void>();
  partnerProgrammeList: any[] = [];
  customerCategoryList: any[] = [];
  userRoleArray: string[] = [];
  editorContent: string = '';
  safeHtmlBody: SafeHtml;
  // sanitizer: Sanitizer;

  constructor(private fb: FormBuilder, private toastr: ToastrService, private sanitizer: DomSanitizer) {
    this.editorConfig = {
      editable: true,
      spellcheck: false,
      height: '10rem',
      minHeight: '5rem',
      placeholder: 'Enter note here...',
      translate: 'no',
      defaultParagraphSeparator: 'p',
      sanitize: false,
      toolbarHiddenButtons: [
        ['fontName', 'heading'],
        [
          'fontSize',
          'textColor',
          'backgroundColor',
          'customClasses',
          'link',
          'unlink',
          'insertImage',
          'insertVideo',
          'insertHorizontalRule',
          'toggleEditorMode',
        ],
      ],
    };
  }

  ngOnInit() {
    // Initialization logic if needed
    this.manageHelpForm = this.fb.group({
      userRole: ['', Validators.required],
      programmeCode: [''],
      customerCategory: [''],
      headerPictureURL: [''],
      headerText: ['', Validators.required],
      tileArray: this.fb.array([]),
    });
    this.addItem(); // Initialize with one tile
    //get role as a collection of array  from helpComponentRoles roles
    this.userRoleArray = this.helpComponentRoles.map((role) => {
      return role.role;
    });

    if (this.isEditMode) {
      this.manageHelpForm.patchValue({
        userRole: this.helpComponentDetails?.userRole || '',
        programmeCode: this.helpComponentDetails?.programmeCode || '',
        customerCategory: this.helpComponentDetails?.customerCategory || '',
        headerPictureURL: this.helpComponentDetails?.headerPictureURL || '',
        headerText: this.helpComponentDetails?.headerText || '',
      });
      this.tileArray.clear();
      this.helpComponentDetails?.tileArray.forEach((tile) => this.addItem(tile));
      this.initialFormValue = this.manageHelpForm.getRawValue();
      this.editorContent = this.helpComponentDetails?.headerText || '';
      this.setupUserRoleChangeListener(this.helpComponentDetails?.userRole || '');
    }
    this.initialFormValue = this.manageHelpForm.getRawValue();
    this.manageHelpForm.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unSubscribe$)).subscribe((val) => {
      this.compareValues();
    });
    this.manageHelpForm
      .get('userRole')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unSubscribe$))
      .subscribe((val) => {
        if (val) {
          this.setupUserRoleChangeListener(val);
        }
      });
  }

  setupUserRoleChangeListener(userRole: string) {
    if (userRole === 'EXTERNAL') {
      this.manageHelpForm.get('programmeCode')?.disable();
      this.manageHelpForm.get('customerCategory')?.enable();
      this.customerCategoryList = this.helpComponentRoles.filter((user) => user.role === 'EXTERNAL')[0]?.customerCategoryArray || [];
      this.manageHelpForm.patchValue({
        programmeCode: '',
        customerCategory: this.helpComponentDetails?.customerCategory || '',
      });
    } else if (userRole === 'PROGRAMME') {
      this.manageHelpForm.get('programmeCode')?.enable();
      this.manageHelpForm.get('customerCategory')?.disable();
      this.partnerProgrammeList = this.helpComponentRoles.filter((user) => user.role === 'PROGRAMME')[0]?.programmeArray || [];
      this.manageHelpForm.patchValue({
        customerCategory: '',
        programmeCode: this.helpComponentDetails?.programmeCode || '',
      });
    } else if (userRole === 'INTERNAL') {
      this.manageHelpForm.get('programmeCode')?.disable();
      this.manageHelpForm.get('customerCategory')?.disable();
      this.manageHelpForm.patchValue({
        programmeCode: '',
        customerCategory: '',
      });
      this.partnerProgrammeList = this.helpComponentRoles.filter((user) => user.role === 'INTERNAL' && user.programmeCode)[0]?.programmeArray || [];
      this.customerCategoryList =
        this.helpComponentRoles.filter((user) => user.role === 'INTERNAL' && user.customerCategory)[0]?.customerCategoryArray || [];
    }
  }

  compareValues() {
    const currentFormValue = this.manageHelpForm.getRawValue();
    if (JSON.stringify(currentFormValue) !== JSON.stringify(this.initialFormValue)) {
      this.invalid = false;
    } else {
      this.invalid = true;
    }
  }

  get tileArray(): FormArray {
    return this.manageHelpForm.get('tileArray') as FormArray;
  }

  addItem(tileData?: any): void {
    const itemGroup = this.fb.group({
      label: [tileData?.label || '', Validators.required],
      url: [tileData?.url || '', Validators.required],
      icon: [tileData?.icon || '', Validators.required],
      loadInNewTab: [tileData?.loadInNewTab || false],
    });
    this.tileArray.push(itemGroup);
  }

  removeItem(index: number): void {
    this.tileArray.removeAt(index);
  }

  ngOnDestroy() {
    // Cleanup logic if needed
  }

  closeModal() {
    // Logic to close the modal
    this.closeActiveModal.emit();
  }

  submitForm() {
    if (this.manageHelpForm.valid) {
      this.formSubmit.emit(this.manageHelpForm.value);
      this.toastr.success('Form submitted successfully');
    } else {
      this.toastr.error('Please fill in all required fields');
    }
  }

  onContentChange(value: string) {
    this.editorContent = this.sanitizer.bypassSecurityTrustHtml(value.toString()).toString();
  }
}
